import { useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Button, Icon, Modal, SearchSelect } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { extractErrorMessage } from "@/services/axiosConfig";
import { invitationService } from "@/services/invitationService";
import { workspaceService } from "@/services/workspaceService";
import { useCnpjLookup } from "@/hooks/useOrganizations";
import { useMembers } from "@/hooks/useInvitations";
import { formatCnpj, formatCpf, stripDocument } from "@/utils/fmt";
import "../organizations/OrgCreateModal.css";

type Mode = "link" | "manual";
type DocType = "cnpj" | "cpf";

interface Props {
  /** Workspace de contexto (plataforma) — usado p/ listar o time TD. */
  workspaceId: number;
  onClose: () => void;
}

/** "Criar novo workspace": enviar link (convite) ou cadastrar manualmente
 * (2 colunas: Configuração TD | Etapa 1 dados do workspace). Fiel ao design. */
export function WorkspaceCreateModal({ workspaceId, onClose }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const cnpjLookup = useCnpjLookup();
  const lastLookedUp = useRef<string>("");
  const { data: members = [] } = useMembers(workspaceId);

  const [mode, setMode] = useState<Mode>("link");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [respId, setRespId] = useState<string>(user ? String(user.id) : "");

  // Link mode
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Manual mode
  const [docType, setDocType] = useState<DocType>("cnpj");
  const [nome, setNome] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [cpf, setCpf] = useState("");
  const [razao, setRazao] = useState("");
  const [uf, setUf] = useState("SP");
  const [ownerNome, setOwnerNome] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [created, setCreated] = useState<string | null>(null);

  const isPj = docType === "cnpj";
  const respOptions = members.map((m) => ({
    id: String(m.user_id),
    label: m.user_name || m.user_email_masked,
    sub: m.role_name,
  }));

  function setDoc(next: DocType) {
    setDocType(next);
    if (next === "cnpj") {
      setCpf("");
    } else {
      setCnpj("");
      setRazao("");
    }
  }

  function onCnpjChange(raw: string) {
    setCnpj(formatCnpj(raw));
    const digits = stripDocument(raw).slice(0, 14);
    if (digits.length === 14 && digits !== lastLookedUp.current) {
      lastLookedUp.current = digits;
      cnpjLookup.mutate(digits, {
        onSuccess: (d) => {
          if (d.razao_social) setRazao(d.razao_social);
          if (d.uf) setUf(d.uf);
        },
      });
    }
  }

  async function generateLink() {
    setError(null);
    setBusy(true);
    try {
      const inv = await invitationService.createWorkspace({
        email: email.trim(),
        workspace_name_suggestion: nome.trim() || undefined,
        acquisition_notes: message.trim() || undefined,
      });
      setLink(inv.accept_url);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function createManual() {
    setError(null);
    setBusy(true);
    try {
      await workspaceService.createManual({
        name: nome.trim(),
        owner_email: ownerEmail.trim(),
        owner_name: ownerNome.trim() || undefined,
        cnpj: isPj ? stripDocument(cnpj) || undefined : undefined,
        uf: uf || undefined,
      });
      await qc.invalidateQueries({ queryKey: ["workspaces", "me"] });
      setCreated(nome.trim());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function copyLink() {
    if (link) void navigator.clipboard?.writeText(link).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const canSendLink = !!email.trim();
  const canCreateManual =
    !!nome.trim() &&
    !!ownerEmail.trim() &&
    (isPj ? stripDocument(cnpj).length === 14 && !!razao.trim() : cpf.replace(/\D/g, "").length === 11);

  const RespField = () => (
    <div className="form-field">
      <span className="orgc-eyebrow">Responsável TD</span>
      <SearchSelect
        value={respId}
        onChange={setRespId}
        options={respOptions}
        placeholder="Selecione o responsável TD…"
        emptyLabel="Nenhum membro encontrado"
      />
      <p className="orgc-hint">
        <strong>Tipo de workspace e plano</strong> são definidos no contrato — não nesse cadastro.
      </p>
    </div>
  );

  // ── Sucesso: link ──────────────────────────────────────────────────────
  if (link) {
    return (
      <Modal
        title="Link de cadastro gerado"
        subtitle="Envie ao cliente — quem aceitar vira owner do workspace."
        size="md"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-link-box">
          <code>{link}</code>
          <Button variant="ghost" size="sm" iconLeft={copied ? "check" : "copy"} onClick={copyLink}>
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <p className="orgc-hint">
          Enviado a <strong>{email}</strong> · o cliente escolhe CNPJ (PJ) ou CPF (PF) no cadastro.
        </p>
      </Modal>
    );
  }

  // ── Sucesso: manual ────────────────────────────────────────────────────
  if (created) {
    return (
      <Modal
        title="Workspace criado"
        subtitle="O owner recebe um e-mail para definir a senha e acessar pela primeira vez."
        size="sm"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-created">
          <span className="orgc-created-icon"><Icon name="check" size={22} /></span>
          <strong>{created}</strong> foi criado · convite de senha enviado a {ownerEmail}.
        </div>
      </Modal>
    );
  }

  // ── Formulário ──────────────────────────────────────────────────────────
  return (
    <Modal
      title="Criar novo workspace"
      subtitle="Escolha como o cliente vai preencher o cadastro. Por link é o caminho recomendado."
      size={mode === "manual" ? "lg" : "md"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {mode === "link" ? (
            <Button variant="primary" iconLeft="send" disabled={!canSendLink || busy} onClick={generateLink}>
              {busy ? "Gerando…" : "Gerar link"}
            </Button>
          ) : (
            <Button variant="primary" iconLeft="check" disabled={!canCreateManual || busy} onClick={createManual}>
              {busy ? "Criando…" : "Criar workspace"}
            </Button>
          )}
        </>
      }
    >
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="orgc-modes">
        <button type="button" className={`orgc-mode ${mode === "link" ? "is-on" : ""}`} onClick={() => setMode("link")}>
          <span className="orgc-mode-icon"><Icon name="send" size={16} /></span>
          <span className="orgc-mode-tag">Recomendado</span>
          <span className="orgc-mode-title">Enviar link</span>
          <span className="orgc-mode-sub">O cliente acessa o link e preenche. Cria o workspace ao final da Etapa 1.</span>
        </button>
        <button type="button" className={`orgc-mode ${mode === "manual" ? "is-on" : ""}`} onClick={() => setMode("manual")}>
          <span className="orgc-mode-icon"><Icon name="plus" size={16} /></span>
          <span className="orgc-mode-title">Cadastrar manualmente</span>
          <span className="orgc-mode-sub">Você preenche a Etapa 1 em nome do cliente. Demais etapas ficam pendentes.</span>
        </button>
      </div>

      {mode === "link" ? (
        <div className="orgc-fields">
          <RespField />
          <label className="form-field">
            <span className="orgc-eyebrow">Enviar para</span>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@empresa.com.br"
              autoFocus
            />
            <p className="orgc-hint">Quem aceitar o convite vira owner do workspace.</p>
          </label>
          <label className="form-field">
            <span className="orgc-eyebrow">Mensagem pessoal (opcional)</span>
            <textarea
              className="form-input"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Olá! Conforme conversamos, segue o acesso pra configurar seu workspace…"
            />
          </label>
        </div>
      ) : (
        <div className="orgc-manual-grid">
          <div className="orgc-manual-col">
            <div className="orgc-manual-col-head">
              <span className="orgc-eyebrow">Configuração TD</span>
              <span className="orgc-col-note">Comercial · só admin TD edita</span>
            </div>
            <div className="orgc-fields">
              <RespField />
            </div>
          </div>

          <div className="orgc-manual-col">
            <div className="orgc-manual-col-head">
              <span className="orgc-eyebrow">Etapa 1 · Dados do workspace</span>
              <span className="orgc-col-note">Demais etapas pendentes</span>
            </div>
            <div className="orgc-fields">
              <div className="form-field">
                <span className="orgc-eyebrow">Tipo de cadastro</span>
                <div className="emp-doc-toggle emp-doc-toggle-lg" role="tablist">
                  <button type="button" className={`emp-doc-tab ${isPj ? "is-on" : ""}`} onClick={() => setDoc("cnpj")}>
                    <Icon name="building" size={13} /> CNPJ <span className="emp-doc-tab-tag">Pessoa Jurídica</span>
                  </button>
                  <button type="button" className={`emp-doc-tab ${!isPj ? "is-on" : ""}`} onClick={() => setDoc("cpf")}>
                    <Icon name="user" size={13} /> CPF <span className="emp-doc-tab-tag">Pessoa Física</span>
                  </button>
                </div>
              </div>

              <label className="form-field">
                <span className="orgc-eyebrow">Nome do workspace</span>
                <input className="form-input" maxLength={120} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: BRF Internal" />
              </label>

              {isPj ? (
                <>
                  <label className="form-field">
                    <span className="orgc-eyebrow">CNPJ da empresa cliente {cnpjLookup.isPending && "· buscando…"}</span>
                    <input className="form-input" inputMode="text" maxLength={18} value={cnpj} onChange={(e) => onCnpjChange(e.target.value)} placeholder="00.000.000/0001-00" />
                  </label>
                  <div className="onb-field-grid onb-grid-3-1">
                    <label className="form-field">
                      <span className="orgc-eyebrow">Razão social</span>
                      <input className="form-input" maxLength={200} value={razao} onChange={(e) => setRazao(e.target.value)} placeholder="—" />
                    </label>
                    <label className="form-field">
                      <span className="orgc-eyebrow">UF</span>
                      <input className="form-input" maxLength={2} value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} />
                    </label>
                  </div>
                </>
              ) : (
                <label className="form-field">
                  <span className="orgc-eyebrow">CPF</span>
                  <input className="form-input" inputMode="numeric" maxLength={14} value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} placeholder="000.000.000-00" />
                </label>
              )}

              <label className="form-field">
                <span className="orgc-eyebrow">Nome do owner</span>
                <input className="form-input" maxLength={120} value={ownerNome} onChange={(e) => setOwnerNome(e.target.value)} placeholder="João da Silva" />
              </label>
              <label className="form-field">
                <span className="orgc-eyebrow">E-mail do owner</span>
                <input className="form-input" type="email" maxLength={120} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="admin@empresa.com.br" />
              </label>

              <div className="orgc-note">
                <Icon name="sparkles" size={13} /> O owner receberá um e-mail para{" "}
                <strong>definir a senha</strong> e acessar o workspace pela primeira vez.
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
